import React, { useEffect, useState } from 'react';
import styles from './styles.module.css';
import Button from '../../components/Button';
import Li from '../../components/Li';
import { IoIosAdd, IoIosClose } from 'react-icons/io';
import { BiTrash } from 'react-icons/bi';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Loader from '../../components/Loader';
import Modal from './Modal';
import { Pagination } from '../../components/Pagination';
import { setPage, getTermsList, deleteTerm, getCurrentTerm } from '../../actions/terms';
import Table from '../../components/Table';
import { Select } from '../../components/ModalInputs';
import { getSchoolYears, getTermGradeTypes } from '../../actions/common';
import Confirmation from '../../components/Confirmation/Confirmation';
import moment from 'moment';
import { formattedDateForData } from '../../utils/smallHelpers';
import Empty from '../../components/Empty'


/**
 * Компонент отображения списка учебных периодов.
 * @returns {JSX.Element} JSX-элемент, представляющий список учебных периодов.
 */
export default function Terms() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { list, pageNumber, pageCount, loading, currentTerm } = useSelector(
    (state) => state.terms
  );
  const { schoolYears, termGradeTypes } = useSelector((state) => state.common);

  const [selectedItem, setSelectedItem] = useState(null);
  const [search, setSearch] = useState({ year: '' });
  const [termForDelete, setTermForDelete] = useState(null);

  // Получение списка и начальная инициализация при монтировании компонента
  useEffect(() => {
    dispatch(setPage(0));
    dispatch(getSchoolYears());
  }, []);

  // Обновление списка терминов при изменении списка
  useEffect(() => {
    if (!list || list.length === 0) {
      setSearch({ year: '' });
    }
  }, [list]);

  // Загрузка списка терминов при изменении номера страницы
  useEffect(() => {
    dispatch(getTermsList());
  }, [pageNumber]);

  /**
   * Открывает модальное окно для просмотра или редактирования данных.
   * @param {object} data Данные для отображения в модальном окне.
   */
  const openModal = (data) => () => {
    setSelectedItem(data || {});
    if (data?.trmid) dispatch(getCurrentTerm(data.trmid));
  };

  /**
   * Закрывает модальное окно.
   */
  const closeModal = () => setSelectedItem(null);

  /**
   * Перенаправляет пользователя на главную страницу.
   */
  const goHome = () => navigate('/');

  /**
   * Обрабатывает изменение значения фильтра.
   * @param {string} name Имя фильтра.
   * @param {string} value Значение фильтра.
   */
  const handleSearch = (name, value) => {
    setSearch((prev) => ({ ...prev, [name]: value }));
    dispatch(getTermsList(value));
  };

  /**
   * Рендерит строку таблицы для каждого элемента списка терминов.
   * @param {object} data Данные для текущего элемента списка.
   * @returns {JSX.Element} JSX-элемент, представляющий строку таблицы.
   */
  const renderLi = (data) => {
    const newData = formattedDateForData(data, 'term_begin', 'term_end');
    const {
      title,
      school_year_name,
      term_begin,
      term_end,
      term_type_name,
      trmid,
    } = newData;

    return (
      <tr key={trmid}>
        <td onClick={openModal(newData)}>
          {school_year_name}
        </td>
        <td onClick={openModal(newData)}>{title}</td>
        <td onClick={openModal(newData)}>
          {moment(term_begin).format('DD.MM.YYYY')}
        </td>
        <td onClick={openModal(newData)}>
          {moment(term_end).format('DD.MM.YYYY')}
        </td>
        <td onClick={openModal(newData)}>{term_type_name}</td>
        <td>
          <i
            className={styles.trash}
            onClick={deleteClickHandler(data)}
          >
            <BiTrash />
          </i>
        </td>
      </tr>
    );
  };

  /**
   * Обработчик клика по кнопке удаления.
   * @param {object} data Данные для удаления.
   * @returns {Function} Функция, которая устанавливает данные для удаления.
   */
  const deleteClickHandler = (data) => (e) => {
    setTermForDelete(data);
  };

  /**
   * Подтверждает удаление выбранного периода.
   */
  const acceptConfirm = async () => {
    const nextYear = await dispatch(deleteTerm(termForDelete, search.year));
    if (nextYear !== search.year) setSearch({ year: nextYear });
    list && !!list.length && setTermForDelete(null);
  };

  /**
   * Отменяет удаление выбранного периода.
   */
  const rejectConfirm = () => {
    setTermForDelete(null);
  };

  const highLow = list && [...list].sort(
    (a, b) => b.trmid - a.trmid
  );

  return (
    <div className={styles.wrapper}>
      {termForDelete && (
        <Confirmation
          title={`Вы уверены что хотите удалить период "${termForDelete.title}"?`}
          acceptConfirmation={acceptConfirm}
          rejectConfirmation={rejectConfirm}
        />
      )}
      <Modal
        closeModal={closeModal}
        data={selectedItem && (selectedItem.trmid ? currentTerm : selectedItem)}
        height={'600px'}
        setSearch={setSearch}
      />
      <h1>Учебные периоды</h1>
      <div className={styles.selectWrapper}>
        <Select
          label="Учебный год"
          name="year"
          onChange={({ target: { name, value } }) =>
            handleSearch(name, value)
          }
          value={search.year}
        >
          {
            [
              { name: 'Все', xp_key: '' },
              ...schoolYears,
            ].map((el) => (
              <option value={el.xp_key}>{el.name}</option>
            ))
          }
        </Select>
      </div>
      {!list || list.length === 0 ?
        <Empty />
        :
        <div style={{ marginTop: '2rem', backgroundColor: 'white' }}>
          <Table>
            <thead>
              <tr>
                <th>Учебный год</th>
                <th>Название</th>
                <th>Начало</th>
                <th>Окончание</th>
                <th>Тип</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {highLow?.map(renderLi)}
            </tbody>
          </Table>
        </div>
      }
      <Button
        style={{ padding: '.5rem 1.5rem' }}
        onClick={openModal()}
      >
        <IoIosAdd size={30} />
        Добавить учебный период
      </Button>
      <footer className={styles.footer}>
        <Button mode="white" onClick={goHome}>
          Закрыть
        </Button>
      </footer>
    </div>
  );
}